/* eslint-disable jsx-a11y/media-has-caption */
import { motion } from "framer-motion";
import { FC, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa6";

interface InlineExpandableAudioPlayerProps {
  audioUrl: string;
  onPlay?: () => void;
  onPause?: () => void;
}

const InlineExpandableAudioPlayer: FC<InlineExpandableAudioPlayerProps> = ({ audioUrl, onPlay, onPause }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const handlePlay = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
      onPlay?.();
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPause?.();
    }
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number): string => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <motion.div
      className="relative"
      onMouseEnter={() => {
        setIsExpanded(true);
        setIsMobile(false);
      }}
      onMouseLeave={() => setIsExpanded(false)}
      onClick={() => {
        setIsMobile(true);
        setIsExpanded(!isExpanded);
      }}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Container with spring animation */}
      <motion.div
        className="bg-white rounded-full shadow-lg overflow-hidden flex items-center cursor-pointer"
        animate={{
          width: isExpanded ? 260 : 40,
          paddingLeft: isExpanded ? 0 : 0,
          paddingRight: isExpanded ? 10 : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 350,
          damping: 35,
          mass: 0.7,
        }}
      >
        {/* Play button - circular and always visible */}
        <motion.button
          onClick={(e) => {
            e.stopPropagation();
            handleTogglePlay();
          }}
          className="w-10 h-10 min-w-10 rounded-full bg-textHeadingColor hover:shadow-xl transition-shadow flex items-center justify-center text-white flex-shrink-0"
        >
          {isPlaying ? <FaPause size={18} /> : <FaPlay size={16} className="ml-0.5" />}
        </motion.button>

        {/* Expanded content */}
        <motion.div
          className="flex-1 flex  items-center justify-center gap-1 px-2"
          initial={{ opacity: 0 }}
          animate={{
            opacity: isExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.2 }}
          // pointerEvents={isExpanded ? "auto" : "none"}
        >
          {/* Progress bar */}
          <button
            className="w-full h-0.5 bg-gray-300 rounded-full cursor-pointer hover:h-1 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              const percentage = (e.clientX - rect.left) / rect.width;
              handleSeek(percentage * duration);
            }}
          >
            <motion.div className="h-full bg-textHeadingColor rounded-full" style={{ width: `${progressPercentage}%` }} />
          </button>

          {/* Status text */}
          {/* Time display */}
          <div className="text-xs text-textColorPrimary font-semibold whitespace-nowrap">
            <span>{formatTime(currentTime)}</span>
            <span className="text-gray-400 mx-0.5">/</span>
            <span>{formatTime(duration)}</span>
          </div>
          {/* <div className="text-xs text-gray-500 whitespace-nowrap">{isPlaying ? "Pause" : "Play"}</div> */}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default InlineExpandableAudioPlayer;
